import { useState } from "react";
import { Button, FormField, Modal, NumberInput, TextInput } from "@a2z/ui";
import { endpoints } from "@/lib/api/endpoints";
import { useTranslate } from "@/lib/i18n";
import { queryKeys, useApiQuery } from "@/shared/queries";
import type {
  AggregatorChoice,
  CustomerSuggestionResponse,
  InvoiceChannel,
} from "@/lib/api/types";
import {
  applySuggestion,
  phoneDigits,
  shouldAskForSuggestion,
  suggestionIsUseful,
} from "../lib/customerSuggestion";
import { useInvoiceDateFormat } from "@/modules/manage/lib/invoiceDisplay";
import {
  undeliverableMessage,
  undeliverableReason,
} from "@/lib/invoice/place";
import {
  UnsavedChangesPrompt,
  useDiscardGuard,
} from "@/shared/UnsavedChangesPrompt";
import { AggregatorChips, PlatformOrderFields } from "./AggregatorChips";

/**
 * STARTING A BILL THAT SITS AT NO TABLE (F1, B9, B46 — unit 4).
 *
 * Until unit 4 every sale had to be seated, and §9 recorded the cost of that
 * since 2026-08-30: the kitchen depletes ingredients for every plate it cooks,
 * but revenue was recorded only for plates attached to a table — so the moment
 * food left on a paper pad the margin report counted its COST and not its
 * REVENUE. In Egypt takeaway and delivery are commonly 40–60% of trade, which
 * makes that the difference between a report that is incomplete and one that is
 * wrong. The workaround on the floor — seating takeaway on a dummy table — then
 * distorted occupancy and average ticket instead.
 *
 * So this dialog is the thing that makes the channel REACHABLE, and it is
 * deliberately the whole of what it is. There is NO delivery lifecycle here: no
 * rider, no dispatch, no out-for-delivery. The founder's direction says it in
 * its own words — "Not a full delivery-management solution."
 *
 * THE FIELDS, AND WHY EACH IS OPTIONAL:
 *
 * · Name and phone (B9) are optional on both channels. A takeaway customer who
 *   will not give a name still gets served, and a bill that refused to open
 *   without one would send the waiter straight back to the dummy table.
 * · The address shows on DELIVERY only — it is the one field a rider cannot do
 *   without, and on a takeaway bag it is a question with no meaning.
 * · The fee (B46) shows on DELIVERY only, and BLANK MEANS THE STORE'S DEFAULT.
 *   That is a different answer from a typed 0, which means this delivery is
 *   free, so the placeholder says which default it would use rather than
 *   pre-filling the box — a pre-filled number reads as a decision somebody
 *   made, and nobody made it. The column is `NOT NULL DEFAULT 0`, so a store that
 *   has never set a fee has ZERO — and blank then means free. The hint says
 *   which of the two it is (B103); it used to promise "the store's default"
 *   with no number behind it either way.
 *
 * OPTIONAL IS NOT THE SAME AS "NOBODY NEEDS IT" (B103). The address carried no
 * "(optional)" marker while both fields above it did, so it read as the one
 * required field and was not; and a DELIVERY could be started with every box
 * blank, which is an order nobody can deliver. Both are now said on the screen
 * — the marker, and a warning when a delivery has neither a phone nor an
 * address. Still a warning: the founder's "not a full delivery-management
 * solution" fences the LIFECYCLE, not the right to know where the food goes.
 * · The aggregator (F2) shows on BOTH channels since T7 (2026-10-06) — a
 *   platform's pickup order is a takeaway with a commission on it, and a
 *   picker that only existed on DELIVERY made every such bag a direct order
 *   on the margin report — and defaults to DIRECT. It is the one field here
 *   that reaches money: the platform's rate is snapshotted onto the bill at
 *   open, and the till sends only the id (see `openInvoicePayload.ts`). Since
 *   T6 it can be corrected while the bill is OPEN, through `ChargesDialog`.
 */
export function OffPremiseDialog({
  channel,
  defaultDeliveryFee,
  isSubmitting,
  onCancel,
  onConfirm,
}: {
  channel: Exclude<InvoiceChannel, "DINE_IN">;
  /** The store's own default, shown as the fee field's placeholder. */
  defaultDeliveryFee: number | null;
  isSubmitting: boolean;
  onCancel: () => void;
  onConfirm: (input: {
    customerName: string;
    customerPhone: string;
    deliveryAddress: string;
    deliveryFeeRaw: string;
    /** F2: `null` is "direct" — the shop's own phone, no commission. */
    aggregatorId: string | null;
    /** T13: the platform's own order number, as typed; meaningless without one. */
    platformOrderRef: string;
  }) => void | Promise<void>;
}) {
  const t = useTranslate();
  const [customerName, setName] = useState("");
  const [customerPhone, setPhone] = useState("");
  const [deliveryAddress, setAddress] = useState("");
  const [deliveryFeeRaw, setFee] = useState("");
  const [aggregatorId, setAggregator] = useState<string | null>(null);
  const [platformOrderRef, setPlatformOrderRef] = useState("");
  const onPlatform = aggregatorId !== null;

  const isDelivery = channel === "DELIVERY";

  /*
   * HAS ANYBODY TOUCHED THIS FORM YET (UX-round D37 · ruling F2, cashier 3.12)?
   * «UX-round Dn» = the 2026-09-17 UX round's own numbering (`.roundtable/2026-09-17-restaurant-ux/decisions.md`), not §11's.
   *
   * The undeliverable warning below is gated on it. A delivery dialog opens
   * with every box blank — that is the only state it can open in — so an
   * unconditional warning fired on arrival, in a red bordered alert, about an
   * omission the cashier had not had the chance to make yet. A warning that is
   * true of the dialog's own starting state is one people learn to look past,
   * and it is the same warning that has to stop them a second later.
   *
   * Dirty, not valid: the moment a name or a fee is typed the cashier has
   * started filling this in, and a delivery still carrying neither a phone nor
   * an address is then a real omission rather than an unstarted form. Still a
   * WARNING and still not a refusal — every field here stays optional, which is
   * what keeps takeaway reachable.
   */
  const isDirty =
    customerName !== "" ||
    customerPhone !== "" ||
    deliveryAddress !== "" ||
    deliveryFeeRaw !== "" ||
    aggregatorId !== null ||
    platformOrderRef !== "";

  /*
   * The platforms, fetched HERE rather than passed down from the floor.
   *
   * This component mounts only when the dialog opens, so the read costs one
   * request per bill started off-premise instead of riding along on every
   * floor refresh — a list that changes a few times a year does not belong in
   * the reads that keep the floor live.
   *
   * On BOTH channels (T7): a platform's pickup order is a takeaway, so the
   * question is asked wherever an off-premise bill starts.
   *
   * Read is `RESTAURANT_MENU_VIEW`, which both WAITER and CASHIER carry — the
   * two presets that can open a bill at all — so this is not a 403 in normal
   * use. It still FAILS SOFT: on an error the picker simply does not render and
   * the bill opens as direct, because a delivery that cannot be taken is worse
   * than one whose commission has to be corrected. `retry: false` makes that a
   * single answer rather than three.
   */
  const aggregators = useApiQuery<AggregatorChoice[]>(
    queryKeys.aggregatorChoices,
    endpoints.restaurant.aggregatorChoices,
    { retry: false },
  );
  // Retired platforms are gone from the picker and still readable on every bill
  // they are already on — that is the whole point of retiring instead of
  // deleting.
  const choices = (aggregators.data ?? []).filter((row) => row.isActive);

  // `isDirty` is already this dialog's own word for "the customer has started
  // answering" — the undeliverable warning is gated on it. The dismissal guard
  // asks the same question, so it reads the same flag rather than a second one.
  const guard = useDiscardGuard(isDirty, onCancel);

  /*
   * T6 / F29 (D150) — «العميل بتاعنا». Once the phone could be a whole mobile,
   * ask the server for the LAST bill with exactly that number. One suggestion
   * with the day it came from (so the cashier asks «لسه على نفس العنوان؟»),
   * applied only by a tap, and only into EMPTY boxes. Keyed on the digits, so
   * a space typed mid-number asks nothing new; fails soft (no suggestion).
   */
  const dateFormat = useInvoiceDateFormat();
  const digits = phoneDigits(customerPhone);
  const suggestionQuery = useApiQuery<CustomerSuggestionResponse>(
    queryKeys.customerSuggestion(digits),
    endpoints.restaurant.customerSuggestion(customerPhone),
    {
      enabled: shouldAskForSuggestion(customerPhone),
      retry: false,
      staleTime: 60_000,
    },
  );
  const suggestion = shouldAskForSuggestion(customerPhone)
    ? (suggestionQuery.data?.suggestion ?? null)
    : null;
  const offerSuggestion =
    suggestion !== null &&
    suggestionIsUseful({ customerName, deliveryAddress }, suggestion, isDelivery);

  const undeliverable = undeliverableReason({
    channel,
    customerPhone,
    deliveryAddress,
  });

  return (
    <>
    <Modal
      isOpen
      onClose={guard.requestClose}
      size="sm"
      title={
        isDelivery
          ? t((m) => m.pos.offPremiseDeliveryTitle)
          : t((m) => m.pos.offPremiseTakeawayTitle)
      }
      closeLabel={t((m) => m.common.close)}
      data-testid="off-premise-dialog"
      footerActions={
        <>
          <Button
            variant="outline"
            size="touch"
            disabled={isSubmitting}
            onClick={guard.requestClose}
            data-testid="off-premise-cancel"
          >
            {t((m) => m.common.cancel)}
          </Button>
          <Button
            size="touch"
            disabled={isSubmitting}
            onClick={() =>
              void onConfirm({
                customerName,
                customerPhone,
                deliveryAddress,
                deliveryFeeRaw,
                aggregatorId,
                platformOrderRef,
              })
            }
            data-testid="off-premise-confirm"
          >
            {t((m) => m.pos.offPremiseStart)}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {/* T6 / F29 — the phone FIRST (the cashier's ask): the number is what a
            regular gives, and it is what finds last time's name and address. */}
        <FormField
          name="customerPhone"
          label={t((m) => m.pos.offPremisePhoneLabel)}
        >
          <TextInput
            name="customerPhone"
            value={customerPhone}
            maxLength={32}
            // LTR on a phone number in both locales: a number typed into an RTL
            // box reorders its own digits on screen, and the person reading it
            // back to a rider is reading it wrong.
            dir="ltr"
            inputMode="tel"
            onChange={(event) => setPhone(event.target.value)}
            data-testid="off-premise-phone"
          />
        </FormField>

        {/* T6 / F29 — the one suggestion, under the number it answers. */}
        {offerSuggestion && suggestion ? (
          <div
            className="flex items-start justify-between gap-2 rounded-[var(--radius-list)] border border-[color:var(--border)] px-3 py-2"
            data-testid="off-premise-suggestion"
          >
            <p className="min-w-0 text-[length:var(--font-size-xs)]">
              <span className="font-semibold">
                {t((m) => m.pos.customerSuggestionLast, {
                  date: dateFormat.businessDate(suggestion.businessDate),
                })}
              </span>{" "}
              {[suggestion.customerName, isDelivery ? suggestion.deliveryAddress : null]
                .filter((part): part is string => Boolean(part))
                .join(" — ")}
            </p>
            <Button
              variant="outline"
              size="sm"
              type="button"
              disabled={isSubmitting}
              data-testid="off-premise-suggestion-use"
              onClick={() => {
                const next = applySuggestion(
                  { customerName, deliveryAddress },
                  suggestion,
                  isDelivery,
                );
                setName(next.customerName);
                setAddress(next.deliveryAddress);
              }}
            >
              {t((m) => m.pos.customerSuggestionUse)}
            </Button>
          </div>
        ) : null}

        <FormField
          name="customerName"
          label={t((m) => m.pos.offPremiseNameLabel)}
        >
          <TextInput
            name="customerName"
            value={customerName}
            maxLength={120}
            onChange={(event) => setName(event.target.value)}
            data-testid="off-premise-name"
          />
        </FormField>


        {isDelivery ? (
          <>
            <FormField
              name="deliveryAddress"
              label={t((m) => m.pos.offPremiseAddressLabel)}
            >
              <TextInput
                name="deliveryAddress"
                value={deliveryAddress}
                maxLength={500}
                onChange={(event) => setAddress(event.target.value)}
                data-testid="off-premise-address"
              />
            </FormField>

            <FormField
              name="deliveryFee"
              label={t((m) => m.pos.offPremiseFeeLabel)}
              /* WHAT "BLANK" ACTUALLY MEANS HERE.

                 The hint promised "the store's default" whether or not the
                 store had one. With none set, blank is ZERO — the delivery
                 goes out free — and a cashier reading the old sentence had
                 every reason to think a number was waiting behind the field.
                 The two cases are now two sentences, and the one with a
                 default says what it is. */
              helperText={
                onPlatform
                  ? // T14 — WITH A PLATFORM, blank is ZERO whatever the store's
                    // default: the platform carries the fee. A typed fee still
                    // wins, so the box stays.
                    t((m) => m.pos.platformFeeHint)
                  : defaultDeliveryFee
                    ? t((m) => m.pos.offPremiseFeeHint, {
                        default: String(defaultDeliveryFee),
                      })
                    : // ZERO and "settings not loaded" both land here, and the
                      // sentence is true of both: blank charges nothing. The
                      // column is `NOT NULL DEFAULT 0`, so zero is what a store
                      // that has never set a fee actually has — which is the
                      // common case, and the one the old wording described as
                      // "the store's default" with no number behind it.
                      t((m) => m.pos.offPremiseFeeHintFree)
              }
            >
              <NumberInput
                name="deliveryFee"
                value={deliveryFeeRaw}
                min={0}
                // A fee is money: 12.50 must be typeable; the payload rounds
                // what is typed to 2 dp (G13).
                allowDecimal
                dir="ltr"
                // The store's own default, as a PLACEHOLDER rather than a
                // value: pre-filling reads as a decision somebody made about
                // this delivery, and nobody made it. On a platform the
                // placeholder is the platform's answer: 0.
                placeholder={
                  onPlatform
                    ? "0"
                    : defaultDeliveryFee === null
                      ? undefined
                      : String(defaultDeliveryFee)
                }
                onChange={(event) => setFee(event.target.value)}
                data-testid="off-premise-fee"
              />
            </FormField>
          </>
        ) : null}

        {/* WHO BROUGHT THE CUSTOMER (F2) — on both channels since T7. The
            chips, their default and their hint are `AggregatorChips`, shared
            with the correction dialog so the two cannot drift. */}
        <AggregatorChips
          choices={choices}
          value={aggregatorId}
          onChange={setAggregator}
          disabled={isSubmitting}
          testIdPrefix="off-premise-aggregator-"
        />
        <PlatformOrderFields
          visible={onPlatform}
          platformOrderRef={platformOrderRef}
          onChange={setPlatformOrderRef}
          disabled={isSubmitting}
          testIdPrefix="off-premise-"
        />

        {/* A DELIVERY NOBODY CAN DELIVER.

            Every field here is optional and stays optional — a takeaway
            customer who will not give a name still gets served, and a bill that
            refused to open would send the waiter back to the dummy table this
            feature exists to end. But a DELIVERY with neither a phone nor an
            address is an order that cannot arrive, and the dialog let one be
            started with every box blank and said nothing at all.

            A warning, not a refusal: the founder's "not a full
            delivery-management solution" fences the LIFECYCLE, and it is not a
            licence to take an order nobody can deliver.

            Since T11 it fires on EITHER gap, and names it: `undeliverableReason`
            is the one rule the correction dialog and the bill read too. */}
        {isDirty && undeliverable ? (
          <p
            role="alert"
            className="rounded-[var(--radius-list)] border border-[color:var(--warning)] px-3 py-2 text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-bold text-[color:var(--warning)]"
            data-testid="off-premise-undeliverable"
            data-reason={undeliverable}
          >
            {undeliverableMessage(undeliverable, t)}
          </p>
        ) : null}

      </div>
    </Modal>
    <UnsavedChangesPrompt
      isOpen={guard.asking}
      onDiscard={guard.discard}
      onKeepEditing={guard.keepEditing}
      testId="off-premise-discard-prompt"
    />
    </>
  );
}
